import { useState } from "react";
import { Check, Info, Loader2 } from "lucide-react";
import { useT } from "../lib/i18n";
import { STATUS_STYLE } from "../lib/options";

export const ChoiceCard = ({ selected, onClick, icon: Icon, label, sub, testid, multi }) => (
  <button type="button" onClick={onClick} data-testid={testid} aria-pressed={selected}
    className={`relative flex w-full min-h-[64px] items-center gap-4 rounded-2xl border-2 p-4 text-left transition-colors ${selected ? "border-[color:var(--orange)] bg-[color:var(--orange-soft)]" : "border-slate-200 bg-white hover:border-slate-400"}`}>
    {Icon && <span className={`grid h-12 w-12 shrink-0 place-items-center rounded-xl ${selected ? "bg-[color:var(--orange)] text-white" : "bg-slate-100 text-[color:var(--navy)]"}`}><Icon size={24} /></span>}
    <span className="min-w-0 flex-1">
      <span className="block break-words text-lg font-bold text-slate-900">{label}</span>
      {sub && <span className="block text-sm text-slate-600">{sub}</span>}
    </span>
    <span className={`grid h-8 w-8 shrink-0 place-items-center border-2 ${multi ? "rounded-lg" : "rounded-full"} ${selected ? "border-[color:var(--orange)] bg-[color:var(--orange)] text-white" : "border-slate-300"}`}>
      {selected && <Check size={18} strokeWidth={3} />}
    </span>
  </button>
);

export const HourCard = ({ selected, onClick, icon, label, help, testid }) => {
  const [open, setOpen] = useState(false);
  const { t } = useT();
  return (
    <div>
      <div className="flex items-stretch gap-2">
        <div className="min-w-0 flex-1"><ChoiceCard multi selected={selected} onClick={onClick} icon={icon} label={label} testid={testid} /></div>
        <button type="button" onClick={() => setOpen(!open)} aria-label={t("explain")} data-testid={`${testid}-info`}
          className="grid w-14 shrink-0 place-items-center rounded-2xl border-2 border-slate-200 text-[color:var(--navy)] hover:bg-slate-50">
          <Info size={22} />
        </button>
      </div>
      {open && <p className="mt-2 rounded-xl bg-slate-50 p-3 text-slate-700 fade-up" data-testid={`${testid}-help`}>{help}</p>}
    </div>
  );
};

export const Empty = ({ icon: Icon, title, sub, children, testid = "empty-state" }) => (
  <div className="card-soft flex flex-col items-center px-6 py-12 text-center fade-up" data-testid={testid}>
    {Icon && <span className="mb-4 grid h-16 w-16 place-items-center rounded-2xl bg-[color:var(--orange-soft)] text-[color:var(--orange)]"><Icon size={32} /></span>}
    <h3 className="text-xl font-bold text-[color:var(--navy)]">{title}</h3>
    {sub && <p className="mt-2 max-w-sm text-slate-600">{sub}</p>}
    {children && <div className="mt-6 flex w-full flex-col items-center gap-3">{children}</div>}
  </div>
);

export const Spinner = ({ label }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-500" data-testid="loading-state">
    <Loader2 className="animate-spin text-[color:var(--orange)]" size={36} />
    {label && <span>{label}</span>}
  </div>
);

export const Chip = ({ status, label }) => (
  <span className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-bold ${STATUS_STYLE[status] || "bg-slate-50 text-slate-700 border-slate-200"}`} data-testid={`status-chip-${status}`}>{label}</span>
);

export const Progress = ({ step, total }) => {
  const { t } = useT();
  return (
    <div data-testid="wizard-progress">
      <div className="mb-2 flex justify-between text-sm font-semibold text-slate-600">
        <span>{t("step")} {step} {t("of")} {total}</span>
      </div>
      <div className="h-3 w-full overflow-hidden rounded-full bg-slate-200">
        <div className="h-full rounded-full bg-[color:var(--orange)] transition-[width] duration-500" style={{ width: `${(step / total) * 100}%` }} />
      </div>
    </div>
  );
};

export const PageTitle = ({ children, sub }) => (
  <div className="mb-6 mt-6">
    <h1 className="text-3xl font-bold tracking-tight text-[color:var(--navy)] sm:text-4xl">{children}</h1>
    {sub && <p className="mt-2 text-slate-600">{sub}</p>}
  </div>
);
